import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, APIMessageResponse } from '@/shared/services/apiConfig';
import { CreateUserData, UpdateUserOrganizationsData, Organization, User } from '@/shared/types/userTypes';
import { PaginatedResponse } from '@/shared/types/responseTypes';

// Use User Accounts Query
export const useUserOrganizations = (userId: string | undefined, regionCode: string | undefined = undefined) => {
  return useQuery({
    queryKey: ['user-organizations', userId, regionCode],
    queryFn: async () => {
      const response = await apiClient.get<PaginatedResponse<Organization>>(
        `/api/v1/users/${userId}/organizations`,
        { params: { regionCode } }
      );
      return response.data.results;
    },
    enabled: !!userId, // Only run query when userId is available
  });
};

// Use Account Users
export const useAccountUsers = () => {
  return useQuery({
    queryKey: ['account-users'],
    queryFn: async () => {
      const response = await apiClient.get<PaginatedResponse<User>>(
        `/api/v1/users`,
      );
      return response.data.results;
    },
  });
};

// Create Account User Mutation
export const useCreateAccountUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userData: CreateUserData) => {
      return await apiClient.post<APIMessageResponse>(
        `/api/v1/users`,
        {
          body: userData,
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['created-user'] });
    },
    onError: (error) => {
      return error;
    },
  });
};

// Update Account User Mutation
export const useChangePassword = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, password }: { userId: string; password: string }) => {
      return await apiClient.put<APIMessageResponse>(
        `/api/v1/users/${userId}/change-password`,
        {
          body: { password },
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-password-changed'] });
    },
    onError: (error) => {
      return error;
    },
  });
};

export const useUpdateUserOrganizations = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, userData }: { userId: string; userData: UpdateUserOrganizationsData }) => {
      return await apiClient.put<APIMessageResponse>(
        `/api/v1/users/${userId}/organizations`,
        {
          body: userData,
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-accounts-updated'] });
    },
    onError: (error) => {
      return error;
    },
  });
};

// Delete Account User Mutation
export const useRemoveUserFromOrganization = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId }: { userId: string }) => {
      return await apiClient.delete<APIMessageResponse>(
        `/api/v1/users/${userId}`
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-deleted'] });
    },
    onError: (error) => {
      return error;
    },
  });
};